import Analytics from '@opencare/analytics';
import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { CreatePartnerPatient } from 'connect/saga/handlers/partner/create-partner-patient';
import { Authenticate } from 'connect/saga/handlers/user/authenticate';
import { selectDataUserAuthenticated } from 'connect/selectors';
import { StepFormWrapper, StepHeader, StepHeaderSubtext, StepHeaderText, StepWrapper } from 'pages/checkout/components/common/layout-styled-components';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';

import { useNextCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';
import { PartnerPatient } from 'pages/checkout/steps/Account/components/PartnerPatient/PartnerPatient';
import { useAnalyticsService } from 'services/AnalyticsService';

import { useIsPartnerUser } from 'utils/customHooks/use-partner-user';
import { useEffectOnce } from 'utils/customHooks/useEffectOnce';

import { AppointmentDetails } from './components/AppointmentDetails/AppointmentDetails';
import { AuthenticationForm } from './components/AuthenticationForm/AuthenticationForm';
import { OtpForm } from './components/AuthenticationForm/OtpForm';
import { LoggedIn } from './components/LoggedIn/LoggedIn';

const analytics = new Analytics();

enum FormState {
  AUTHENTICATION,
  OTP_VERIFICATION,
  LOGGED_IN,
  PARTNER_PATIENT,
}

export const Account = () => {
  const analyticsService = useAnalyticsService();

  // AuthenticationForm will call this saga handler, so we can watch it here to determine if we need to show the OTP form
  const authenticateResponse = useSelector(Authenticate.value!.selector);

  const isPartnerUser = useIsPartnerUser();
  const isPartnerPatientCreated = useSelector(CreatePartnerPatient.success!.selector);

  const otpSent = useMemo(
    () => authenticateResponse?.otpSent || false,
    [authenticateResponse],
  );

  const isUserAuthenticated = useSelector(selectDataUserAuthenticated);
  const isUserAuthenticatedOnLoad = useRef(isUserAuthenticated);

  const checkoutState = useSelector(CheckoutStateHandler.selector);
  const email = useMemo(() => checkoutState?.account?.email || '', [checkoutState?.account?.email]);

  const { goToNextStep } = useNextCheckoutStep();

  // If the user was not authenticated on load, but is now authenticated, go to the next step
  useEffect(() => {
    // Unset isUserAuthenticatedOnLoad if the user logged out
    if (!isUserAuthenticated) {
      isUserAuthenticatedOnLoad.current = false;
    }

    if (isUserAuthenticatedOnLoad.current === false && isUserAuthenticated) {
      goToNextStep();
    }

    // Continue if the partner user has created a patient account.
    if (isPartnerUser && isPartnerPatientCreated) {
      goToNextStep();
    }
  }, [
    isUserAuthenticated,
    isPartnerUser,
    isPartnerPatientCreated,
    goToNextStep,
  ]);

  useEffectOnce(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-info',
      name: 'checkout',
      section: 'account-info',
      ...analyticsService.analyticsData,
    });
  });

  const formState = useMemo(() => {
    if (isPartnerUser) {
      return FormState.PARTNER_PATIENT;
    }

    if (isUserAuthenticated) {
      return FormState.LOGGED_IN;
    }

    if (otpSent && email) {
      return FormState.OTP_VERIFICATION;
    }

    return FormState.AUTHENTICATION;
  }, [isUserAuthenticated, otpSent, email, isPartnerUser]);

  const authForm = useMemo(
    () => ({
      [FormState.AUTHENTICATION]: <AuthenticationForm />,
      [FormState.OTP_VERIFICATION]: <OtpForm />,
      [FormState.LOGGED_IN]: <LoggedIn />,
      [FormState.PARTNER_PATIENT]: <PartnerPatient />,
    }[formState]),
    [formState],
  );

  return (
    <StepWrapper>
      <StepFormWrapper>
        <StepHeader>
          <StepHeaderText>
            {isPartnerUser
              ? 'Book an appointment'
              : 'Book your appointment'}
          </StepHeaderText>
          <StepHeaderSubtext>
            {isPartnerUser
              ? "We will keep the patient's information safe and secure."
              : 'We will keep your information safe and secure.'}
          </StepHeaderSubtext>
        </StepHeader>

        <AppointmentDetails />

        {authForm}
      </StepFormWrapper>
    </StepWrapper>
  );
};
